import { z } from 'zod'
import type { CreateSheetFlowSnapshot } from '../pages/app/createSheetFlowStorage'

const choice = z.object({ option: z.string(), startDate: z.string(), endDate: z.string() })
const file = z.object({ id: z.string().min(1), title: z.string() })
const snapshot = z
  .object({
    draft: z
      .object({
        destination: z.enum(['new', 'template', 'existing']),
        accounts: z.array(
          z.object({
            id: z.string(),
            label: z.string(),
            itemId: z.string().optional(),
            institutionName: z.string().optional(),
          })
        ),
        ranges: z.record(z.string(), z.object({ start: z.string(), end: z.string() })),
        exportOption: z.enum(['single-tab', 'monthly-summary', 'account-breakdown']),
        tabName: z.string(),
        title: z.string(),
        existingSpreadsheetId: z.string(),
        existingSpreadsheetTitle: z.string(),
      })
      .passthrough(),
    dateChoices: z.record(z.string(), choice),
    editAllTogether: z.boolean().optional(),
    bankScopes: z.record(z.string(), z.enum(['bank', 'accounts'])).optional(),
    createNewTab: z.enum(['power-templates', 'new-spreadsheets', 'google-drive']),
    nicknames: z.record(z.string(), z.string()),
  })
  .passthrough()
const createNewTab = z.enum(['power-templates', 'new-spreadsheets', 'google-drive'])
const actionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('template') }),
  z.object({ kind: z.literal('new') }),
  z.object({ kind: z.literal('drive') }),
  z.object({ kind: z.literal('browse'), tab: createNewTab.optional() }),
  z.object({ kind: z.literal('existing'), file }),
  z.object({ kind: z.literal('resume'), step: z.enum(['date-range', 'export-options']), snapshot }),
])
export type GoogleCreateAction =
  | Exclude<z.infer<typeof actionSchema>, { kind: 'resume' }>
  | {
      kind: 'resume'
      step: 'date-range' | 'export-options'
      snapshot: CreateSheetFlowSnapshot
    }
const sessionSchema = z.object({
  uid: z.string().min(1),
  nonce: z.string().uuid(),
  createdAt: z.number(),
  action: actionSchema,
  outcome: z.enum(['pending', 'connected', 'cancelled', 'failed']),
  returnEntryKey: z.string().optional(),
})
const KEY = 'v1_google_reconnect'
const MAX_AGE = 30 * 60_000
export const GOOGLE_CREATE_RETURN = '/v1/app/dashboard?view=new'

export function readGoogleReconnectSession() {
  try {
    const result = sessionSchema.safeParse(JSON.parse(sessionStorage.getItem(KEY) || 'null'))
    if (
      !result.success ||
      Date.now() - result.data.createdAt > MAX_AGE ||
      result.data.createdAt > Date.now()
    )
      return null
    return result.data
  } catch {
    return null
  }
}

export function beginGoogleReconnect(
  uid: string,
  action: GoogleCreateAction,
  returnEntryKey?: string
): string {
  const session = sessionSchema.parse({
    uid,
    nonce: crypto.randomUUID(),
    createdAt: Date.now(),
    action,
    outcome: 'pending',
    returnEntryKey,
  })
  // Deliberately throws on a full/unavailable store: do not promise saved choices
  // and then leave the document without them.
  sessionStorage.setItem(KEY, JSON.stringify(session))
  return `${GOOGLE_CREATE_RETURN}&googleReconnect=${session.nonce}`
}

export function nonceFromGoogleReturn(path: string): string | null {
  if (!path.startsWith(`${GOOGLE_CREATE_RETURN}&googleReconnect=`)) return null
  const nonce = path.slice(`${GOOGLE_CREATE_RETURN}&googleReconnect=`.length)
  return z.string().uuid().safeParse(nonce).success ? nonce : null
}

export function finishGoogleReconnect(
  uid: string,
  nonce: string,
  outcome: 'connected' | 'cancelled' | 'failed'
) {
  const session = readGoogleReconnectSession()
  if (!session || session.uid !== uid || session.nonce !== nonce)
    throw new Error('Google connection session expired. Please return to Create New and try again.')
  sessionStorage.setItem(KEY, JSON.stringify({ ...session, outcome }))
}

export function takeGoogleReconnect(uid: string, nonce: string, allowBrowserBack = false) {
  const session = readGoogleReconnectSession()
  if (
    !session ||
    session.uid !== uid ||
    session.nonce !== nonce ||
    (session.outcome === 'pending' && !allowBrowserBack)
  )
    return null
  sessionStorage.removeItem(KEY)
  return session.outcome === 'pending' ? { ...session, outcome: 'cancelled' as const } : session
}
